import { useEffect, useMemo, useState } from 'react'
import { useSupabaseAuth } from '../../../lib/useSupabaseAuth'
import { supabase, createCompanyForUser } from '../../../lib/supabase'
import { logger } from '../../../lib/logger'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const APPLE_RELAY_DOMAIN = 'privaterelay.appleid.com'

const isAppleRelayEmail = (email) =>
  typeof email === 'string' && email.toLowerCase().endsWith(`@${APPLE_RELAY_DOMAIN}`)

const CompanyInfoModal = () => {
  const { user, refreshCompanyId } = useSupabaseAuth()
  const meta = user?.user_metadata || {}

  const defaultEmail = useMemo(() => {
    if (meta.company_email && EMAIL_RE.test(meta.company_email)) return meta.company_email
    if (user?.email && !isAppleRelayEmail(user.email)) return user.email
    return ''
  }, [meta.company_email, user?.email])

  const [companyName, setCompanyName] = useState(meta.company_name || '')
  const [companyEmail, setCompanyEmail] = useState(defaultEmail)
  const [firstName, setFirstName] = useState(meta.first_name || '')
  const [lastName, setLastName] = useState(meta.last_name || '')
  const [phone, setPhone] = useState(meta.phone || '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    const trimmed = {
      companyName: companyName.trim(),
      companyEmail: companyEmail.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
    }

    if (!trimmed.companyName) return setError('Company name is required.')
    if (!trimmed.companyEmail) return setError('Company email is required.')
    if (!EMAIL_RE.test(trimmed.companyEmail)) return setError('Please enter a valid email address.')
    if (isAppleRelayEmail(trimmed.companyEmail)) {
      return setError('Please enter a real business email — Apple private relay addresses cannot be used here.')
    }
    if (!trimmed.firstName) return setError('Owner first name is required.')
    if (!trimmed.lastName) return setError('Owner last name is required.')

    setSubmitting(true)
    try {
      const provider = user?.app_metadata?.provider || meta.signup_source || 'website'
      const { data: updated, error: updateErr } = await supabase.auth.updateUser({
        data: {
          ...meta,
          company_name: trimmed.companyName,
          company_email: trimmed.companyEmail,
          first_name: trimmed.firstName,
          last_name: trimmed.lastName,
          phone: trimmed.phone,
          signup_source: provider,
          pending_company_creation: true,
        },
      })
      if (updateErr) throw updateErr

      const refreshedUser = updated?.user || {
        ...user,
        user_metadata: {
          ...meta,
          company_name: trimmed.companyName,
          company_email: trimmed.companyEmail,
          first_name: trimmed.firstName,
          last_name: trimmed.lastName,
          phone: trimmed.phone,
          signup_source: provider,
          pending_company_creation: true,
        },
      }

      const newCompanyId = await createCompanyForUser(refreshedUser)
      if (!newCompanyId) {
        throw new Error('We could not create your company. Please try again or contact support.')
      }

      await refreshCompanyId()
      // Gate unmounts automatically once companyId is set in auth context.
    } catch (err) {
      logger?.error?.('[CompanyInfoModal] Error creating company:', err)
      setError(err.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e) => {
    // Block Escape so the modal can't be dismissed.
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-info-modal-title"
    >
      <div className="bg-elevated rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-hidden border border-mute">
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-blue-700 text-white px-6 py-5">
          <h2 id="company-info-modal-title" className="text-xl font-bold">
            Tell us about your business
          </h2>
          <p className="text-sm text-brand-blue-100 mt-1">
            This information will be displayed on your estimates and invoices.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label htmlFor="company-name" className="block text-sm font-medium text-primary mb-1.5">
              Company name <span className="text-red-400">*</span>
            </label>
            <input
              id="company-name"
              type="text"
              required
              autoFocus
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              tabIndex={0}
              aria-label="Company name"
              className="w-full px-3 py-2.5 bg-input border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              placeholder="Acme Roofing & Gutters"
            />
          </div>

          <div>
            <label htmlFor="company-email" className="block text-sm font-medium text-primary mb-1.5">
              Company email <span className="text-red-400">*</span>
            </label>
            <input
              id="company-email"
              type="email"
              required
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              tabIndex={0}
              aria-label="Company email"
              className="w-full px-3 py-2.5 bg-input border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              placeholder="hello@yourcompany.com"
            />
            <p className="mt-1 text-xs text-muted">Shown to clients on estimates and invoices.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="owner-first" className="block text-sm font-medium text-primary mb-1.5">
                Owner first name <span className="text-red-400">*</span>
              </label>
              <input
                id="owner-first"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                tabIndex={0}
                aria-label="Owner first name"
                className="w-full px-3 py-2.5 bg-input border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                placeholder="Jane"
              />
            </div>
            <div>
              <label htmlFor="owner-last" className="block text-sm font-medium text-primary mb-1.5">
                Owner last name <span className="text-red-400">*</span>
              </label>
              <input
                id="owner-last"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                tabIndex={0}
                aria-label="Owner last name"
                className="w-full px-3 py-2.5 bg-input border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label htmlFor="owner-phone" className="block text-sm font-medium text-primary mb-1.5">
              Phone <span className="text-muted font-normal">(optional)</span>
            </label>
            <input
              id="owner-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              tabIndex={0}
              aria-label="Phone number"
              className="w-full px-3 py-2.5 bg-input border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              placeholder="(555) 123-4567"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-900/30 border border-red-500/40 p-3">
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            tabIndex={0}
            aria-label="Save and continue"
            className="w-full px-4 py-3 bg-brand-blue-500 text-white font-semibold rounded-lg hover:bg-brand-blue-600 focus:outline-none focus:ring-2 focus:ring-brand-blue-400 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Creating your company...
              </span>
            ) : (
              'Save and continue'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CompanyInfoModal
